import { useState } from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { GitMerge, Scissors, Trash2, Undo2, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { CATEGORIES, type Concept, type CurationDecision } from '../api/knowledgeApi'
import { useCurateConcept, useRecordRule, useRevokeRule } from '../hooks/useKnowledge'
import { ConceptPicker } from './ConceptPicker'
import { WeightLabel } from './ArtworkTile'
import { WEIGHTS, describeRule } from './ruleText'

const AUTO = '__auto'
const BUILD = '__build'

/** Category and search weight: settings that take effect in AMS at once. */
export function ConceptSettings({ concept }: { concept: Concept }) {
  const curate = useCurateConcept(concept.ucid)
  const suggestion = concept.suggestedCategory && !concept.curatorCategory && !concept.buildCategory
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Category and weight</CardTitle>
        <CardDescription>
          Take effect immediately. Weight is how much the concept counts when ranking search results.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-xs text-slate-500">
            Category{concept.buildCategory && <> (build says “{concept.buildCategory}”)</>}
          </label>
          <Select
            value={concept.curatorCategory ?? BUILD}
            onValueChange={(v) => curate.mutate({ category: v === BUILD ? null : v })}
            disabled={curate.isPending}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={BUILD}>{concept.buildCategory ? `Build's: ${concept.buildCategory}` : 'None (uncategorised)'}</SelectItem>
              {CATEGORIES.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {suggestion && (
            <Button
              size="sm"
              variant="outline"
              className="w-full"
              disabled={curate.isPending}
              onClick={() => curate.mutate({ category: concept.suggestedCategory, fromSuggestion: true })}
            >
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              Accept suggestion: {concept.suggestedCategory}
              {concept.suggestedConfidence != null && (
                <span className="ml-1 text-slate-400">({Math.round(concept.suggestedConfidence * 100)}%)</span>
              )}
            </Button>
          )}
        </div>
        <div className="space-y-1.5">
          <label className="flex items-center justify-between text-xs text-slate-500">
            <span>Search weight</span>
            <span>
              now <WeightLabel weight={concept.weight} curated={concept.curatorWeight != null} />
            </span>
          </label>
          <Select
            value={concept.curatorWeight == null ? AUTO : String(concept.curatorWeight)}
            onValueChange={(v) => curate.mutate({ weight: v === AUTO ? null : Number(v) })}
            disabled={curate.isPending}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={AUTO}>
                Automatic{concept.specificity != null ? ` (${concept.specificity.toFixed(2)}, from rarity)` : ''}
              </SelectItem>
              {WEIGHTS.map((w) => (
                <SelectItem key={w.value} value={String(w.value)}>{w.label}</SelectItem>
              ))}
              {concept.curatorWeight != null && !WEIGHTS.some((w) => w.value === concept.curatorWeight) && (
                <SelectItem value={String(concept.curatorWeight)}>Custom ({concept.curatorWeight.toFixed(2)})</SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  )
}

const RULE_LABEL: Record<string, string> = { merge: 'Merge', separate: 'Split', reject: 'Remove' }

export function RuleStatus({ rule }: { rule: CurationDecision }) {
  if (rule.revokedAt) return <span className="text-xs text-slate-400">revoked</span>
  if (rule.appliedStatus) {
    const tone = rule.appliedStatus === 'applied' || rule.appliedStatus === 'already' ? 'text-emerald-700' : 'text-amber-700'
    return (
      <span className={`text-xs ${tone}`} title={rule.appliedRunId ?? undefined}>
        {rule.appliedStatus.replace('_', ' ')}{rule.appliedEffect ? `: ${rule.appliedEffect}` : ''}
      </span>
    )
  }
  return (
    <span className="text-xs text-slate-500">
      {rule.exportedAt ? 'sent to the build, waiting for the next run' : 'waiting for tonight’s export'}
    </span>
  )
}

/**
 * Structural changes: merge with other concepts, split names off, remove. These are rules the
 * nightly graph build applies, so they show as pending until it has.
 */
export function ConceptStructure({ concept, rules }: { concept: Concept; rules: CurationDecision[] }) {
  const record = useRecordRule()
  const revoke = useRevokeRule()
  const [mergeOpen, setMergeOpen] = useState(false)
  const [mergeWith, setMergeWith] = useState<Array<{ ucid: string; name: string; category: string | null; artworkCount: number }>>([])
  const [splitOpen, setSplitOpen] = useState(false)
  const [split, setSplit] = useState<string[]>([])
  const [rejectOpen, setRejectOpen] = useState(false)
  const [reason, setReason] = useState('')
  const pendingReject = rules.some((r) => r.action === 'reject' && !r.appliedStatus)

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm">Structure</CardTitle>
        <CardDescription>
          Merges, splits and removals are rules for the nightly graph build (03:30 UTC). They apply on
          the next build and keep applying on every build after.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" onClick={() => setMergeOpen(true)}>
            <GitMerge className="mr-1.5 h-3.5 w-3.5" /> Merge with…
          </Button>
          <Button size="sm" variant="outline" disabled={concept.memberNames.length < 2} onClick={() => setSplitOpen(true)}>
            <Scissors className="mr-1.5 h-3.5 w-3.5" /> Split names off
          </Button>
          <Button size="sm" variant="outline" className="text-rose-700" disabled={pendingReject} onClick={() => setRejectOpen(true)}>
            <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Remove
          </Button>
        </div>

        {rules.length > 0 && (
          <div className="space-y-2">
            {rules.map((r) => (
              <div key={r.id} className="rounded border bg-slate-50 p-2 text-sm">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <span className="mr-1 rounded bg-white px-1.5 py-0.5 text-[10px] font-medium uppercase text-slate-600">
                      {RULE_LABEL[r.action]}
                    </span>
                    {describeRule(r)}
                  </div>
                  <Button size="sm" variant="ghost" className="h-7 px-2" disabled={revoke.isPending} onClick={() => revoke.mutate(r.id)}>
                    <Undo2 className="mr-1 h-3.5 w-3.5" /> Revoke
                  </Button>
                </div>
                <div className="mt-1 flex justify-between gap-2">
                  <RuleStatus rule={r} />
                  <span className="text-[11px] text-slate-400">{formatDistanceToNow(new Date(r.decidedAt), { addSuffix: true })}</span>
                </div>
              </div>
            ))}
            <Link to="/app/knowledge/rules" className="text-xs text-slate-500 hover:underline">All rules →</Link>
          </div>
        )}
      </CardContent>

      <Dialog open={mergeOpen} onOpenChange={(o) => { setMergeOpen(o); if (!o) setMergeWith([]) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Merge into “{concept.name}”</DialogTitle>
            <DialogDescription>
              From the next build on, every name of the concepts you pick resolves to one concept with
              this one. Its links and artworks combine.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {mergeWith.map((c, i) => (
              <ConceptPicker key={c.ucid} value={c} onChange={() => setMergeWith(mergeWith.filter((_, j) => j !== i))} />
            ))}
            <ConceptPicker
              value={null}
              exclude={[concept.ucid, ...mergeWith.map((c) => c.ucid)]}
              placeholder={mergeWith.length ? 'Add another…' : 'Find the concept to merge in…'}
              onChange={(c) => c && setMergeWith([...mergeWith, c])}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMergeOpen(false)}>Cancel</Button>
            <Button
              disabled={!mergeWith.length || record.isPending}
              onClick={() =>
                record.mutate(
                  { action: 'merge', ucid: concept.ucid, ucids: mergeWith.map((c) => c.ucid) },
                  { onSuccess: () => { setMergeOpen(false); setMergeWith([]) } }
                )
              }
            >
              Record merge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={splitOpen} onOpenChange={(o) => { setSplitOpen(o); if (!o) setSplit([]) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Split names off “{concept.name}”</DialogTitle>
            <DialogDescription>
              Tick the names that do not belong. From the next build on they are never clustered with
              the rest again; they become their own concept.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 space-y-1.5 overflow-auto">
            {concept.memberNames.map((m) => (
              <label key={m} className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={split.includes(m)}
                  onCheckedChange={(v) => setSplit(v ? [...split, m] : split.filter((x) => x !== m))}
                />
                {m}
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSplitOpen(false)}>Cancel</Button>
            <Button
              disabled={!split.length || split.length === concept.memberNames.length || record.isPending}
              onClick={() =>
                record.mutate({ action: 'separate', ucid: concept.ucid, names: split }, { onSuccess: () => { setSplitOpen(false); setSplit([]) } })
              }
            >
              Split {split.length || ''} off
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove “{concept.name}” from the graph?</AlertDialogTitle>
            <AlertDialogDescription>
              The next build drops this concept and all {concept.memberNames.length} of its names, so
              its {concept.artworkCount.toLocaleString()} artwork links disappear. To keep it but stop it
              counting in search, set its weight to Generic or hide it instead. You can revoke this.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea rows={2} placeholder="Why (optional)" value={reason} onChange={(e) => setReason(e.target.value)} />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700"
              onClick={() => record.mutate({ action: 'reject', ucid: concept.ucid, reason: reason.trim() || undefined })}
            >
              Remove on next build
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  )
}
